/* eslint-disable */
import type { Category } from "../model/categories.pb";
import type { InvestmentEntity, InvestmentEntityAnnouncement } from "../model/investment_entities.pb";

export enum InvestmentEntityField {
  TOKEN = "TOKEN",
  SYMBOL = "SYMBOL",
  DISPLAY_NAME = "DISPLAY_NAME",
  TYPE = "TYPE",
  ICON_URL = "ICON_URL",
  TOTAL_NUM_SHARES = "TOTAL_NUM_SHARES",
  STATUS = "STATUS",
  COLOR = "COLOR",
  VISIBILITY = "VISIBILITY",
  TRADABILITY = "TRADABILITY",
  RELEASED_AT = "RELEASED_AT",
  VERSION = "VERSION",
  ABOUT_TEXT = "ABOUT_TEXT",
  KEYWORDS = "KEYWORDS",
  CEO = "CEO",
  INDUSTRY = "INDUSTRY",
  NUMBER_OF_EMPLOYEES = "NUMBER_OF_EMPLOYEES",
  HEADQUARTERS = "HEADQUARTERS",
  DARK_ICON_URL = "DARK_ICON_URL",
  DARK_COLOR = "DARK_COLOR",
  DW_ACTIVATED_AT = "DW_ACTIVATED_AT",
  FIRST_PRICE_AVAILABLE_AT = "FIRST_PRICE_AVAILABLE_AT",
  RELEASE_STAGE = "RELEASE_STAGE",
  FULL_SECURITY_NAME = "FULL_SECURITY_NAME",
  CUSIP = "CUSIP",
}

export interface GetAllInvestmentEntitiesRequest {
  /**
   * The newest token of the range to sync. Use null to fetch a range that includes
   * the most recent sync entities.
   */
  newest_token?: string;
  /**
   * The oldest token of the range to sync. Use null to fetch a range that includes
   * sync entities going back to the beginning of time.
   */
  oldest_token?: string;
  /**
   * The specific fields in the InvestmentEntity to return in the response. If empty,
   * all fields will be returned.
   */
  entity_fields?: InvestmentEntityField[];
}

export interface GetAllInvestmentEntitiesResponse {
  /**
   * The newest token of the range returned. If the request included a non-null newest_token, it
   * will always be reflected back in this field.
   */
  newest_token?: string;
  /**
   * The oldest token of the range returned. If the entire requested range was fulfilled, this
   * field will reflect the oldest_token of the request.
   */
  oldest_token?: string;
  /**
   * When next_request is populated, the server could not fit all requested entities into this
   * response. The client should call back with the next_request to fetch the remaining entities.
   */
  next_request?: GetAllInvestmentEntitiesRequest;
  entities?: InvestmentEntity[];
  refresh_all_entities?: boolean;
}

export interface GetInvestmentEntityDetailRequest {
  /** The investment entity to return the details for. */
  investment_entity_token?: string;
}

export interface GetInvestmentEntityDetailResponse {
  announcements?: InvestmentEntityAnnouncement[];
}

export interface GetDiscoveryRequest {
}

export interface GetDiscoveryResponse {
  categories?: Category[];
}

export interface GetInvestmentEntityRequest {
  /** The investment entity to return for. */
  investment_entity_token?: string;
  symbol?: string;
}

export interface GetInvestmentEntityResponse {
  /** The investment entity. */
  investment_entity?: InvestmentEntity;
}

export interface SetCategoryInvestmentEntitiesRequest {
  /** Identifier for category to be updated. */
  category_token?: string;
  /** List of investment entity tokens to set for this category. */
  investment_entity_tokens?: string[];
}

export interface CreateInvestmentEntityRequest {
  /** The Investment Entity to create. Should be sparsely populated with the necessary data */
  investment_entity?: InvestmentEntity;
  /** A list of tokens of the categories to which the new Investment Entity should belong */
  category_tokens?: string[];
}

export interface CreateInvestmentEntityResponse {
  /** The created Investment Entity. */
  investment_entity?: InvestmentEntity;
  /** The Categories associated with the newly-created Investment Entity. */
  categories?: Category[];
}

export interface SearchCategoriesRequest {
  /**
   * A filter by which to search categories. if not provided, all categories should be
   * returned.
   */
  filter?: SearchCategoriesRequestSearchCategoryFilter;
}

export interface SearchCategoriesRequestSearchCategoryFilter {
  /**
   * A list of category types (should probably be an enum- currently a string in
   * category proto)
   */
  types?: string[];
}

export interface SearchCategoriesResponse {
  /** The list of categories matching the filter */
  categories?: Category[];
  /** A human-readable error message */
  error_message?: string;
}

export interface BatchGetCategoriesByTokensRequest {
  /** The list of tokens for categories to be fetched */
  category_tokens?: string[];
}

export interface BatchGetCategoriesByTokensResponse {
  /** The categories for the provided tokens */
  categories?: { [key: string]: Category };
}

export interface BatchGetCategoriesByTokensResponseCategoriesEntry {
  key: string;
  value?: Category;
}

export interface SyncInvestmentEntityAboutDataRequest {
  /** The token of the Investment Entity to sync */
  investment_entity_token?: string;
  /** Whether to update the about text (about text tends to require more oversight, so this is false by default) */
  update_about_text?: boolean;
}

export interface SyncInvestmentEntityAboutDataResponse {
  /** The Investment Entity before updating */
  investment_entity_before?: InvestmentEntity;
  /** The Investment Entity after updating */
  investment_entity_updated?: InvestmentEntity;
  /** A human-readable error message */
  error_message?: string;
}

export interface ExportXigniteRecommendationsRequest {
  /** The list of symbols to export recommendations */
  symbols?: string[];
}

export interface ExportXigniteRecommendationsResponse {
  /** The csv for the provided symbols */
  csv?: string;
}

export interface InvestmentEntityService {
  GetAllInvestmentEntities(request: GetAllInvestmentEntitiesRequest): Promise<GetAllInvestmentEntitiesResponse>;
  GetDiscovery(request: GetDiscoveryRequest): Promise<GetDiscoveryResponse>;
  GetInvestmentEntity(request: GetInvestmentEntityRequest): Promise<GetInvestmentEntityResponse>;
  CreateInvestmentEntity(request: CreateInvestmentEntityRequest): Promise<CreateInvestmentEntityResponse>;
}

export interface InternalInvestmentEntityService {
  update(request: InvestmentEntity): Promise<InvestmentEntity>;
}
